import { DefaultToastOptions, toast } from 'react-hot-toast'
import { motion } from 'framer-motion'

const toastStyles = `
  .toast-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    pointer-events: none;
  }
  
  .toast-content {
    pointer-events: auto;
  }
`

interface TP extends DefaultToastOptions {
    message: string
    state?: 'success' | 'error' | 'info' | 'warning'
}

export const toastHandler = ({ message, state = 'success', ...props }: TP) => {
    let bgColor = 'bg-indigo-600' // Default brand indigo
    let icon = '✓'
    let iconBg = 'bg-white/20'
    const iconColor = 'text-white'

    if (state === 'success') {
        bgColor = 'bg-gradient-to-r from-indigo-600 to-violet-700'
        icon = '✓'
    } else if (state === 'error') {
        bgColor = 'bg-red-500'
        icon = '✕'
        iconBg = 'bg-red-600/40'
    } else if (state === 'warning') {
        bgColor = 'bg-amber-500'
        icon = '!'
        iconBg = 'bg-amber-600/40'
    } else if (state === 'info') {
        bgColor = 'bg-indigo-500'
        icon = 'i'
        iconBg = 'bg-indigo-600/40'
    }

    return toast.custom(
        t => (
            <>
                <style dangerouslySetInnerHTML={{ __html: toastStyles }} />

                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: t.visible ? 1 : 0, y: t.visible ? 0 : -20, scale: t.visible ? 1 : 0.9 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className='toast-container'
                >
                    <div
                        onClick={e => {
                            e.stopPropagation()
                            toast.remove(t.id)
                        }}
                        className={`toast-content cursor-pointer px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-[350px] ${bgColor} ${iconColor} text-base font-medium`}
                        data-testid='toast'
                        data-overlay='true'
                    >
                        <div className='flex items-center'>
                            <div className={`p-1 mr-4 ${iconBg} rounded-full bg-opacity-30`}>
                                <div className='flex items-center justify-center w-6 h-6 text-white rounded-full'>
                                    {icon}
                                </div>
                            </div>
                            <div>{message}</div>
                        </div>
                    </div>
                </motion.div>
            </>
        ),
        {
            duration: 2000,
            position: 'top-right',
            ...props,
        }
    )
}

export const customToast = ({
    message,
    action,
    actionText,
    duration = 4000,
}: {
    message: string
    action: () => void
    actionText: string
    duration?: number
}) => {
    return toast.custom(
        t => (
            <div className='toast-container'>
                <div
                    className='toast-content bg-green-700 text-white p-2 rounded-md flex items-center justify-between gap-4 w-[300px]'
                    data-testid='custom-toast'
                    data-overlay='true'
                >
                    {message}
                    <button
                        className='p-1 text-sm font-semibold text-green-700 bg-white rounded-md'
                        onClick={e => {
                            e.stopPropagation()
                            action()
                            toast.dismiss(t.id)
                        }}
                    >
                        {actionText}
                    </button>
                </div>
            </div>
        ),
        { duration }
    )
}
